// React / Next
import { useState, useEffect, MutableRefObject } from 'react';
// App
import { HorizontalScrollOptions } from './horizontal-scroll.interfaces';

/**
 * Hook for managing horizontal scroll
 */
const useHorizontalScroll = (
  containerRef: MutableRefObject<HTMLDivElement>,
  shouldLoad: boolean,
  options: HorizontalScrollOptions = {
    scrollStep: 200,
    scrollPositionTreshold: 20,
  }
): {
  scrollPosition: 'left' | 'middle' | 'right';
  scrollRight: () => void;
  scrollLeft: () => void;
} => {
  const [scrollPosition, setScrollPosition] = useState(null);
  const { scrollStep, scrollPositionTreshold } = options;

  /**
   * Inits scroll listener
   */
  useEffect(() => {
    if (!containerRef || !containerRef.current) return;

    containerRef.current.addEventListener('scroll', onScroll);
  }, []);

  /**
   * Inits scroll position
   */
  useEffect(() => {
    if (!shouldLoad || !containerRef || !containerRef.current) return;

    if (containerRef.current.scrollWidth > containerRef.current.offsetWidth) {
      setScrollPosition('left');
    }
  }, [shouldLoad]);

  /**
   * Event fired when user scrolls on x axis
   * @param event the scroll event
   */
  const onScroll = () => {
    const containerElem = containerRef.current;

    if (!containerElem) return;

    if (containerElem.scrollLeft <= scrollPositionTreshold) {
      setScrollPosition('left');
    } else if (
      containerElem.scrollLeft >=
      containerElem.scrollWidth - containerElem.offsetWidth - scrollPositionTreshold
    ) {
      setScrollPosition('right');
    } else if (containerElem.scrollLeft <= containerElem.scrollWidth) {
      setScrollPosition('middle');
    }
  };

  /**
   * Scrolls container to right
   */
  const scrollRight = () =>
    containerRef.current.scrollBy({
      top: 0,
      left: scrollStep,
      behavior: 'smooth',
    });

  /**
   * Scrolls container to left
   */
  const scrollLeft = () =>
    containerRef.current.scrollBy({
      top: 0,
      left: -scrollStep,
      behavior: 'smooth',
    });

  return { scrollPosition, scrollRight, scrollLeft };
};

export default useHorizontalScroll;
